import React, { useContext, useEffect, useState } from "react";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AutoAwesomeOutlined,
  ChatOutlined,
  Close,
  Logout,
  LightbulbOutlined,
  MicOutlined,
  Menu as MenuIcon,
  MenuBookOutlined,
  RecordVoiceOverOutlined,
} from "@mui/icons-material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import PortalBreadcrumbs from "./PortalBreadcrumbs";
import {
  getAllReviewUpgradedLARForAdmin,
  getAllReviewVoiceNotesForAdmin,
  getWinPremiumSubmissions,
} from "../service/service";

const drawerWidth = 296;
const navItems = [
  { label: "Characters", path: "/characters", icon: <AutoAwesomeOutlined /> },
  { label: "Courses", path: "/dashboard", icon: <MenuBookOutlined /> },
  { label: "Tip of the Day", path: "/tip-of-day", icon: <LightbulbOutlined /> },
  {
    label: "Record & Earn",
    path: "/record-and-earn",
    icon: <MicOutlined />,
    count: "recordings",
  },
  {
    label: "Voice notes",
    path: "/review-voice-notes",
    icon: <RecordVoiceOverOutlined />,
    count: "voiceNotes",
  },
  {
    label: "Upgraded LAR",
    path: "/review-upgraded-lar",
    icon: <ChatOutlined />,
    count: "upgradedLar",
  },
  {
    label: "AI feedback",
    path: "/review-ai-feedbacks",
    icon: <AutoAwesomeOutlined />,
  },
];

const DrawerMenu = ({ children }) => {
  const { logout, authState } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [counts, setCounts] = useState({ voiceNotes: 0, upgradedLar: 0, recordings: 0 });

  useEffect(() => {
    const loadCounts = async () => {
      try {
        const [voiceNotes, upgradedLar, recordings] = await Promise.all([
          getAllReviewVoiceNotesForAdmin(),
          getAllReviewUpgradedLARForAdmin(),
          // Only the pending count is needed, so ask for a single row.
          getWinPremiumSubmissions({ status: "pending", page: 1, limit: 1 }),
        ]);
        setCounts({
          voiceNotes: Number(voiceNotes) || 0,
          upgradedLar: Number(upgradedLar) || 0,
          recordings: Number(recordings?.data?.counts?.pending) || 0,
        });
      } catch {
        setCounts({ voiceNotes: 0, upgradedLar: 0, recordings: 0 });
      }
    };
    if (open) loadCounts();
  }, [open]);

  const logoutUser = () => {
    logout();
    navigate("/");
  };
  const isActive = (path) =>
    location.pathname === path ||
    (path === "/dashboard" && location.pathname.startsWith("/edit/"));
  const pendingCount = counts.voiceNotes + counts.upgradedLar + counts.recordings;
  const navigation = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          px: 2.5,
          py: 2.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar
            alt="Accentify mascot"
            src="/tify/listening-head.png"
            variant="rounded"
            sx={{
              width: 42,
              height: 42,
              bgcolor: "transparent",
            }}
          />
          <Box>
            <Typography fontWeight={800}>Accentify</Typography>
            <Typography variant="caption" color="text.secondary">
              Admin portal
            </Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={() => setOpen(false)}
          sx={{ display: { md: "none" } }}
        >
          <Close />
        </IconButton>
      </Box>
      <Divider />
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ px: 2.5, pt: 2, pb: 1 }}
      >
        Workspace
      </Typography>
      <List sx={{ px: 1.25, py: 0 }}>
        {navItems.map((item) => (
          <ListItemButton
            key={item.path}
            component={Link}
            to={item.path}
            selected={isActive(item.path)}
            onClick={() => setOpen(false)}
            sx={{
              mb: 0.5,
              borderRadius: 2,
              "&.Mui-selected": {
                bgcolor: "primary.lighterShade",
                color: "text.primary",
                "& .MuiListItemIcon-root": { color: "primary.dark" },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 38, color: "text.secondary" }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontWeight: isActive(item.path) ? 700 : 500,
              }}
            />
            {item.count && (
              <Box sx={{ mr: 1 }}>
                <Badge
                  badgeContent={counts[item.count]}
                  color="secondary"
                  max={99}
                >
                  <Box sx={{ width: 8, height: 8 }} />
                </Badge>
              </Box>
            )}
          </ListItemButton>
        ))}
      </List>
      <Box sx={{ mt: "auto", p: 2 }}>
        <Divider sx={{ mb: 1.5 }} />
        <ListItemButton onClick={logoutUser} sx={{ borderRadius: 2 }}>
          <ListItemIcon sx={{ minWidth: 38 }}>
            <Logout />
          </ListItemIcon>
          <ListItemText primary="Sign out" />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: "100vh", display: "flex" }}>
      <AppBar position="fixed" elevation={0}>
        <Toolbar sx={{ px: { xs: 2, md: 3 } }}>
          <Tooltip title="Open navigation">
            <IconButton
              onClick={() => setOpen(true)}
              edge="start"
              sx={{ mr: 1 }}
            >
              <Badge badgeContent={pendingCount} color="secondary" max={99}>
                <MenuIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <Typography fontWeight={800} sx={{ flexGrow: 1 }}>
            Accentify Admin
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: { xs: "none", sm: "block" } }}
          >
            {authState?.email || authState?.role || "Admin"}
          </Typography>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="temporary"
        open={open}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            borderRight: "1px solid #E2EAE4",
          },
        }}
      >
        {navigation}
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: { xs: 2, sm: 3, lg: 5 },
          py: { xs: 3, md: 4 },
          mt: "64px",
          maxWidth: 1680,
          mx: "auto",
          width: "100%",
        }}
      >
        <PortalBreadcrumbs />
        {children}
      </Box>
    </Box>
  );
};

export default DrawerMenu;
